import {Random} from "@dp-instant/utils";
type GainBalance = [Level: number, Value: number];
type Balance = {
    Start: number;
    GainBalance: GainBalance[];
};
const MathfLerp = (x: number, y: number, t: number) => x * t + y * (1 - t);
export class MetaConfig {
    private _levelFinishReward: Balance = {
        Start: 200,
        GainBalance: [[0, 160]]
    };

    private _startPopulation: Balance = {
        Start: 10,
        GainBalance: [[0, 1]]
    };
    private _generationSpeed: Balance = {
        Start: 1,
        GainBalance: [
            [0, 0.073],
            [151, 0.079]
        ]
    };
    private _offlineEarning: Balance = {
        Start: 100,
        GainBalance: [
            [0, 75],
            [10, 150],
            [20, 300],
            [30, 375],
            [40, 450],
            [50, 675],
            [60, 750],
            [70, 825],
            [80, 900],
            [90, 975],
            [100, 1050],
            [110, 1500],
            [120, 2000],
            [130, 2500]
        ]
    };
    private _boostersCost: Balance = {
        Start: 50,
        GainBalance: [
            [0, 95],
            [20, 155],
            [40, 250]
        ]
    };
    private _neutralRate: Balance = {
        Start: 1,
        GainBalance: [
            [10, 1.7],
            [20, 2.4],
            [30, 3.1],
            [40, 3.8],
            [50, 4.6],
            [60, 5.4],
            [70, 6],
            [80, 6.8],
            [90, 7.5],
            [100, 8.2]
        ]
    };
    private _neutralStart: Balance = {
        Start: 10,
        GainBalance: [
            [10, 20],
            [20, 30],
            [30, 40],
            [40, 50],
            [50, 60],
            [60, 70],
            [70, 80]
        ]
    };
    private _neutralCap: Balance = {
        Start: 10,
        GainBalance: [
            [10, 20],
            [20, 30],
            [30, 40],
            [40, 50],
            [50, 60],
            [60, 70],
            [70, 60]
        ]
    };
    private _commonCap: Balance = {
        Start: 50,
        GainBalance: [
            [1, 50],
            [20, 80],
            [30, 100],
            [40, 120]
        ]
    };
    private _boostersCostTable: Balance = {
        Start: 0,
        GainBalance: [
            [0, 40000],
            [0, 50000],
            [0, 70000],
            [0, 85000],
            [0, 100000],
            [0, 150000],
            [0, 200000],
            [0, 250000],
            [0, 300000],
            [0, 350000],
            [0, 400000],
            [0, 450000],
            [0, 500000],
            [0, 550000],
            [0, 600000],
            [0, 650000],
            [0, 700000],
            [0, 750000],
            [0, 800000],
            [0, 850000],
            [0, 900000],
            [0, 950000],
            [0, 1000000],
            [0, 1050000],
            [0, 1100000],
            [0, 1150000],
            [0, 1200000],
            [0, 1250000],
            [0, 1300000],
            [0, 1350000],
            [0, 1400000],
            [0, 1450000],
            [0, 1500000],
            [0, 1550000],
            [0, 1600000],
            [0, 1650000],
            [0, 1700000],
            [0, 1750000],
            [0, 1800000],
            [0, 1850000],
            [0, 1900000],
            [0, 1950000],
            [0, 2000000],
            [0, 2050000],
            [0, 2100000],
            [0, 2150000],
            [0, 2200000],
            [0, 2250000],
            [0, 2300000],
            [0, 2350000]
        ]
    };
    private _botUpgrade: number = 1.5;
    private _botRandomOffset: number = 2;

    // Random random = new Random(); TODO: add random.seed

    public GetLevelFinishReward(level: number) {
        return this.GetBalanceValueByLevelIntegral(level, this._levelFinishReward);
    }

    public GetStartPopulation(level: number) {
        // return level + 9;
        return this.GetBalanceValueByLevelIntegral(level, this._startPopulation);
    }
    public GetGenerationSpeed(level: number) {
        return this.GetBalanceValueByLevelIntegral(level, this._generationSpeed);
    }

    public GetOfflineEarning(level: number) {
        if (level <= 150) return this.GetBalanceValueByLevelIntegral(level, this._offlineEarning);

        return Math.ceil(0.8 * level) + 150275;
    }

    public GetBoosterCost(level: number): number {
        if (level <= 150) return this.GetBalanceValueByLevelIntegral(level, this._boostersCost);
        if (level <= 200) return this.GetBalanceFromTable(level - 151, this._boostersCostTable);
        return Math.pow(level, 3) + this.GetBoosterCost(200);
    }

    GetBalanceFromTable(level: number, boostCostTable: Balance) {
        if (level <= 0 || level >= boostCostTable.GainBalance.length) {
            const [_, Value] = boostCostTable.GainBalance[0];
            return Value;
        }
        const [_, Value] = boostCostTable.GainBalance[level];
        return Value;
    }

    public GetNeutralRate(level: number) {
        return this.GetBalanceValueByLevelStairs(level, this._neutralRate);
    }

    public GetNeutralCap(level: number) {
        return this.GetBalanceValueByLevelStairs(level, this._neutralCap);
    }

    public GetCommonCap(level: number) {
        return this.GetBalanceValueByLevelStairs(level, this._commonCap);
    }

    public GetNeutralStartPopulation(level: number) {
        return this.GetBalanceValueByLevelStairs(level, this._neutralStart);
    }

    public GetBotUpgradeLevelsCap(level: number, botUpgradeOverride?: number) {
        const upgrade = botUpgradeOverride ?? this._botUpgrade;
        if (level == 1) return Math.max(1, Math.ceil(upgrade));

        const newBotLevel =
            Math.ceil(level * upgrade) +
            Random.from(-this._botRandomOffset, this._botRandomOffset);
        return newBotLevel;
    }

    GetBalanceValueByLevelIntegral(level: number, balance: Balance): number {
        var value = balance.Start;
        var startFrom = 1;
        for (let i = 0; i < balance.GainBalance.length; i++) {
            var [gainLevel, gainValue] = balance.GainBalance[i];
            if (level >= gainLevel) {
                var maxLevel = Number.MAX_SAFE_INTEGER;
                if (i + 1 < balance.GainBalance.length) {
                    const [Level] = balance.GainBalance[i + 1];
                    maxLevel = Level;
                }

                var gainLevel = Math.min(maxLevel, level);
                var diff = gainLevel - startFrom;
                value += diff * gainValue;
                startFrom = maxLevel;
            } else break;
        }

        return value;
    }

    GetBalanceValueByLevelStairs(level: number, balance: Balance): number {
        const start = balance.Start;
        for (let i = 0; i < balance.GainBalance.length - 1; i++) {
            var [gainLevel, gainValue] = balance.GainBalance[i];
            if (i == 0 && level < gainLevel) return start;
            const [Level, _] = balance.GainBalance[i + 1];
            if (gainLevel <= level && level < Level) return gainValue;
        }
        const [_, Value] = balance.GainBalance[balance.GainBalance.length - 1];
        return Value;
    }

    GetBalanceValueByLevelInterpolate(level: number, balance: Balance): number {
        var start = balance.Start;

        var len = balance.GainBalance.length;
        if (len <= 1) {
            var [curBalanceLevel, curBalanceValue] =
                balance.GainBalance[balance.GainBalance.length - 1];
            // return Mathf.LerpUnclamped(balance.Start, curBalance.Value, level / curBalance.Level);
            // FIXME: Mathf.LerpUnclamped ?
            return MathfLerp(balance.Start, curBalanceValue, level / curBalanceLevel);
        }

        for (let i = 0; i < balance.GainBalance.length - 1; i++) {
            var [gainLevel, gainValue] = balance.GainBalance[i];
            var [nextLevel, nextValue] = balance.GainBalance[i + 1];

            if (i == 0 && gainLevel > level) {
                var t = level / gainLevel;
                return MathfLerp(start, gainValue, t);
            }

            if (gainLevel >= level && level < nextLevel) {
                var t = level / (nextLevel - gainLevel);
                return MathfLerp(gainValue, nextValue, t);
            }
        }

        var [_, curValue] = balance.GainBalance[balance.GainBalance.length - 1];
        return curValue;
    }
}
